// src/pages/network/SavedPosts.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, Bookmark, Search, AlertCircle, 
  SortAsc, Filter, X
} from 'lucide-react';
import { db } from '../../firebase/config';
import { 
  collection, query, where, getDocs, orderBy, limit,
  doc, getDoc, deleteDoc
} from 'firebase/firestore';
import PostList from '../../components/network/feed/PostList';

const SavedPosts = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [savedPosts, setSavedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  
  // Redirect if not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: '/network/saved' } });
    }
  }, [currentUser, navigate]);
  
  // Fetch saved posts
  useEffect(() => {
    const fetchSavedPosts = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Query saved posts reference collection
        const savedPostsQuery = query(
          collection(db, 'savedPosts'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        
        const savedPostsSnap = await getDocs(savedPostsQuery);
        
        // Fetch the actual post data
        const postsPromises = savedPostsSnap.docs.map(async (savedDoc) => {
          const savedData = savedDoc.data();
          const postRef = doc(db, 'posts', savedData.postId);
          const postSnap = await getDoc(postRef);
          
          if (postSnap.exists()) {
            const postData = postSnap.data();
            
            // Get author data
            const authorRef = doc(db, 'businesses', postData.authorId);
            const authorSnap = await getDoc(authorRef);
            
            return {
              id: postSnap.id,
              ...postData,
              savedId: savedDoc.id,
              savedAt: savedData.createdAt?.toDate() || new Date(),
              createdAt: postData.createdAt?.toDate() || new Date(),
              author: authorSnap.exists() ? {
                id: authorSnap.id,
                ...authorSnap.data()
              } : { 
                id: postData.authorId,
                name: 'Unknown Business'
              },
              userHasSaved: true
            };
          }
          return null;
        });
        
        const posts = (await Promise.all(postsPromises)).filter(Boolean);
        setSavedPosts(posts);
      } catch (err) {
        console.error('Error fetching saved posts:', err);
        setError('Failed to load saved posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSavedPosts();
  }, [currentUser]);
  
  // Handle removing from saved
  const handleUnsave = async (postId, savedId) => {
    try {
      // Delete the saved reference
      await deleteDoc(doc(db, 'savedPosts', savedId));
      
      // Update local state
      setSavedPosts(prev => prev.filter(post => post.id !== postId));
    } catch (err) {
      console.error('Error removing saved post:', err);
    }
  };

  const handleUpdatePost = (updatedPost) => {
    setSavedPosts((prev) => prev.map((post) => (post.id === updatedPost.id ? { ...post, ...updatedPost } : post)));
  };

  const handleDeletePost = (postId) => {
    setSavedPosts((prev) => prev.filter((post) => post.id !== postId));
  };
  
  // Filter and sort posts
  const getFilteredAndSortedPosts = () => {
    // Filter by search term
    let filtered = savedPosts;
    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort posts
    return filtered.sort((a, b) => {
      if (sortOrder === 'newest') {
        return b.savedAt - a.savedAt;
      } else if (sortOrder === 'oldest') {
        return a.savedAt - b.savedAt;
      } else if (sortOrder === 'popular') {
        return (b.likeCount || 0) - (a.likeCount || 0);
      }
      return 0;
    });
  };
  
  const filteredPosts = getFilteredAndSortedPosts();
  
  if (!currentUser) {
    return null; // Handled by the redirect
  }
  
  return (
    <div className="container mx-auto max-w-4xl px-4">
      <div className="mb-6">
        <Link 
          to="/network"
          className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Network
        </Link>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900 flex items-center">
            <Bookmark size={20} className="mr-2 text-[#190192]" />
            Saved Posts
          </h1>
          <p className="text-gray-600">Posts you've bookmarked for later reference</p>
        </div>
        
        {/* Search and filters */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Search saved posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="relative">
              <SortAsc className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <select
                className="pl-10 pr-4 py-2.5 rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20 appearance-none"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Content area */}
        <div>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-6 text-center">
              <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Posts</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-[#2C0DB5]"
              >
                Try Again
              </button>
            </div>
          ) : savedPosts.length === 0 ? (
            <div className="p-6 text-center py-16">
              <Bookmark size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No saved posts yet</h3>
              <p className="text-gray-500 max-w-md mx-auto mb-6">
                You haven't saved any posts yet. Browse the network and bookmark posts you'd like to reference later.
              </p>
              <Link
                to="/network"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Explore Network
              </Link>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="p-6 text-center py-16">
              <Search size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No matching posts</h3>
              <p className="text-gray-500 mb-4">
                No posts match your search criteria.
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <X size={16} className="mr-1" />
                Clear Search
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              <PostList 
                posts={filteredPosts} 
                onUnsave={handleUnsave}
                isAuthenticated={true}
                isSavedView={true}
                onUpdatePost={handleUpdatePost}
                onDeletePost={handleDeletePost}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedPosts;
